import { LATEST_BOUNDARY } from "./boundaryCatalog";

export const CALCULATOR_STORAGE_KEY = "ibcalculator_state";
export const CALCULATOR_STORAGE_VERSION = 1;
export const DEFAULT_CALCULATOR_BOUNDARY = LATEST_BOUNDARY;
export const DEFAULT_CALCULATOR_TIMEZONE = "tz1";

export const DEFAULT_CORE_DATA = {
  corePoints: null,
  tokGrade: null,
  eeGrade: null,
};

const hasStorage = () =>
  typeof window !== "undefined" &&
  typeof window.localStorage !== "undefined";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const readCalculatorState = () => {
  if (!hasStorage()) return null;

  try {
    const rawState = window.localStorage.getItem(CALCULATOR_STORAGE_KEY);
    if (!rawState) return null;

    const parsed = JSON.parse(rawState);
    return isObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeCalculatorState = (state) => {
  if (!hasStorage()) return false;

  try {
    window.localStorage.setItem(CALCULATOR_STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
};

export const clearCalculatorState = () => {
  if (!hasStorage()) return;

  try {
    window.localStorage.removeItem(CALCULATOR_STORAGE_KEY);
  } catch {
    // Storage can fail in private browsing modes. Reset should still update UI.
  }
};

const pathParts = (path) => path.split(".").filter(Boolean);

export const hasValueAtPath = (source, path) => {
  const parts = pathParts(path);
  let cursor = source;

  for (const part of parts) {
    if (!isObject(cursor) || !Object.prototype.hasOwnProperty.call(cursor, part)) {
      return false;
    }

    cursor = cursor[part];
  }

  return true;
};

export const getValueAtPath = (source, path, fallback) => {
  const parts = pathParts(path);
  let cursor = source;

  for (const part of parts) {
    if (!isObject(cursor) || !Object.prototype.hasOwnProperty.call(cursor, part)) {
      return fallback;
    }

    cursor = cursor[part];
  }

  return cursor;
};

export const setValueAtPath = (source, path, value) => {
  const parts = pathParts(path);
  if (parts.length === 0) return source;

  const setNext = (cursor, index) => {
    const part = parts[index];
    const currentObject = isObject(cursor) ? cursor : {};

    if (index === parts.length - 1) {
      return {
        ...currentObject,
        [part]: value,
      };
    }

    return {
      ...currentObject,
      [part]: setNext(currentObject[part], index + 1),
    };
  };

  return setNext(source, 0);
};

export const areStoredValuesEqual = (a, b) => {
  if (Object.is(a, b)) return true;

  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const hasMeaningfulValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value !== "";
  if (typeof value === "number" || typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (!isObject(value)) return false;

  return Object.entries(value).some(([key, nestedValue]) => {
    if (key === "version") return false;
    if (key === "boundary" && nestedValue === DEFAULT_CALCULATOR_BOUNDARY) return false;
    if (key === "timezone" && nestedValue === DEFAULT_CALCULATOR_TIMEZONE) return false;
    return hasMeaningfulValue(nestedValue);
  });
};

export const hasMeaningfulCalculatorState = (state) =>
  isObject(state) && hasMeaningfulValue(state);

export const createSerializableCalculatorState = (state) => ({
  version: CALCULATOR_STORAGE_VERSION,
  boundary: getValueAtPath(state, "boundary", DEFAULT_CALCULATOR_BOUNDARY),
  timezone: getValueAtPath(state, "timezone", DEFAULT_CALCULATOR_TIMEZONE),
  core: getValueAtPath(state, "core", {}),
  groups: getValueAtPath(state, "groups", {}),
  grades: getValueAtPath(state, "grades", {}),
  coreData: getValueAtPath(state, "coreData", DEFAULT_CORE_DATA),
});

export const readCalculatorValue = (path, fallback) =>
  getValueAtPath(readCalculatorState() || {}, path, fallback);
